import React from 'react';
import { Route, Routes } from 'react-router-dom';
import LandingPage from '../pages/LandingPage';
import RegisterEntryPage from '../pages/RegisterEntryPage';
import OnboardingPage from '../pages/OnboardingPage';
import LoginPage from '../pages/LoginPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import NotFoundPage from '../pages/NotFoundPage';
import HowItWorksPage from '../pages/HowItWorksPage';
import DriverInstantTripsGuidePage from '../pages/DriverInstantTripsGuidePage';
import HowItWorksComingSoonPage from '../pages/HowItWorksComingSoonPage';
import DriverOnboardingGuidePage from '../pages/DriverOnboardingGuidePage';
import OAuthRedirectPage from '../pages/OAuthRedirectPage';
import AuthErrorPage from '../pages/AuthErrorPage';
import AccessDeniedPage from '../pages/AccessDeniedPage';
import AccountReadyPage from '../pages/AccountReadyPage';
import ScheduleBookingPage from '../pages/ScheduleBookingPage';
import DriverProfileSetupPage from '../pages/DriverProfileSetupPage';
import CompanyOwnerOnboardingPage from '../pages/CompanyOwnerOnboardingPage';
import CompanyOwnerDashboardPage from '../pages/CompanyOwnerDashboardPage';
import CompanyOwnerDriverDetailPage from '../pages/CompanyOwnerDriverDetailPage';
import CompanyOwnerPayoutSetupPage from '../pages/CompanyOwnerPayoutSetupPage';
import CompanyOwnerRoute from '../components/auth/CompanyOwnerRoute';
import CustomerRoute from '../components/auth/CustomerRoute';
import AccountPage from '../pages/AccountPage';
import TripDetailPage from '../pages/TripDetailPage';
import TripRecordPage from '../pages/TripRecordPage';
import BookingConfirmPage from '../pages/BookingConfirmPage';
import BookingSearchingPage from '../pages/BookingSearchingPage';
import PaymentReturnPage from '../pages/PaymentReturnPage';
import PublicTrackingPage from '../pages/PublicTrackingPage';
import AdminRoute from '../components/auth/AdminRoute';
import AdminDashboardPage from '../pages/AdminDashboardPage';
import AdminEngagementDashboardPage from '../pages/AdminEngagementDashboardPage';
import AdminDeclaredLoadsPage from '../pages/AdminDeclaredLoadsPage';
import AdminDriverCampaignsPage from '../pages/AdminDriverCampaignsPage';
import AdminCustomerPromotionsPage from '../pages/AdminCustomerPromotionsPage';
import AdminCommunicationsPage from '../pages/AdminCommunicationsPage';
import AdminReviewQueuePage from '../pages/AdminReviewQueuePage';
import AdminFaceLivenessReviewsPage from '../pages/AdminFaceLivenessReviewsPage';
import AdminFaceLivenessReviewDetailPage from '../pages/AdminFaceLivenessReviewDetailPage';
import AdminBusinessProfilesPage from '../pages/AdminBusinessProfilesPage';
import AdminDriverCoveragePage from '../pages/AdminDriverCoveragePage';
import AdminDriverOnboardingAssistPage from '../pages/AdminDriverOnboardingAssistPage';
import AdminDriversPage from '../pages/AdminDriversPage';
import AdminDriverReviewPage from '../pages/AdminDriverReviewPage';
import AdminUsersPage from '../pages/AdminUsersPage';
import AdminFinancePage from '../pages/AdminFinancePage';
import AdminPaymentOpsPage from '../pages/AdminPaymentOpsPage';
import AdminPaymentIntentDetailPage from '../pages/AdminPaymentIntentDetailPage';
import AdminTripsPage from '../pages/AdminTripsPage';
import AdminTripDetailPage from '../pages/AdminTripDetailPage';
import AdminWhatsAppBookingsPage from '../pages/AdminWhatsAppBookingsPage';
import AdminPayoutReviewsPage from '../pages/AdminPayoutReviewsPage';
import AdminSupportPage from '../pages/AdminSupportPage';
import AdminOnboardingPage from '../pages/AdminOnboardingPage';
import AboutPage from '../pages/AboutPage';
import PrivacyPolicyPage from '../pages/PrivacyPolicyPage';
import TermsPage from '../pages/TermsPage';
import DataDeletionPage from '../pages/DataDeletionPage';

const AppRouter: React.FC = () => {
    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/data-deletion" element={<DataDeletionPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/how-it-works/driver-app/instant-trips" element={<DriverInstantTripsGuidePage />} />
            <Route
                path="/how-it-works/driver-app/offer-trips"
                element={
                    <HowItWorksComingSoonPage
                        appName="Driver App guide"
                        guideName="Offer Trips"
                        description="This guide will explain the Offers page, Offer Details, scheduled jobs, and how accepted work moves into trip progress."
                    />
                }
            />
            <Route
                path="/how-it-works/user-app/instant-booking"
                element={
                    <HowItWorksComingSoonPage
                        appName="User App guide"
                        guideName="Instant Booking"
                        description="This guide will explain how customers request a delivery that should start as soon as a driver accepts it."
                    />
                }
            />
            <Route
                path="/how-it-works/user-app/scheduled-booking"
                element={
                    <HowItWorksComingSoonPage
                        appName="User App guide"
                        guideName="Scheduled Booking"
                        description="This guide will explain how customers schedule a delivery for a later date or time."
                    />
                }
            />
            <Route path="/driver-onboarding-guide" element={<DriverOnboardingGuidePage />} />
            <Route path="/register" element={<RegisterEntryPage />} />
            <Route path="/oauth2/redirect" element={<OAuthRedirectPage />} />
            <Route path="/auth/error" element={<AuthErrorPage />} />
            <Route path="/access-denied" element={<AccessDeniedPage />} />
            <Route path="/account-ready" element={<AccountReadyPage />} />
            <Route path="/track/:token" element={<PublicTrackingPage />} />
            <Route path="/admin/onboarding" element={<AdminOnboardingPage />} />
            <Route path="/schedule-booking" element={<ScheduleBookingPage />} />
            <Route path="/driver/account-ready" element={<DriverProfileSetupPage />} />
            {/* Signed-in customer area. The guard also enforces the feature
                flag, so while web booking is off this route redirects home
                rather than existing as a half-built page. */}
            <Route element={<CustomerRoute />}>
                <Route path="/account" element={<AccountPage />} />
                <Route path="/account/trips/:tripId" element={<TripDetailPage />} />
                {/* The record, not the live map. Every lane — scheduled, in
                    progress, past — reaches the same page, because a delivery's
                    details do not change shape with where it is in its life.
                    Separate from the tracking route above rather than a mode
                    flag on it: one answers "where is it right now", the other
                    "what is this delivery". */}
                <Route path="/account/trips/:tripId/details" element={<TripRecordPage />} />
                <Route path="/book/confirm" element={<BookingConfirmPage />} />
                <Route path="/book/searching" element={<BookingSearchingPage />} />
                <Route path="/payments/tokenized" element={<PaymentReturnPage />} />
            </Route>
            <Route element={<CompanyOwnerRoute />}>
                <Route path="/company/onboarding" element={<CompanyOwnerOnboardingPage />} />
                <Route path="/company/payout-setup" element={<CompanyOwnerPayoutSetupPage />} />
                <Route path="/company/dashboard" element={<CompanyOwnerDashboardPage />} />
                <Route path="/company/drivers/:driverId" element={<CompanyOwnerDriverDetailPage />} />
            </Route>
            <Route element={<AdminRoute />}>
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/engagement" element={<AdminEngagementDashboardPage />} />
                <Route path="/admin/declared-loads" element={<AdminDeclaredLoadsPage />} />
                <Route path="/admin/driver-campaigns" element={<AdminDriverCampaignsPage />} />
                <Route path="/admin/customer-promotions" element={<AdminCustomerPromotionsPage />} />
                <Route path="/admin/communications" element={<AdminCommunicationsPage />} />
                <Route path="/admin/review-queue" element={<AdminReviewQueuePage />} />
                <Route path="/admin/face-liveness-reviews" element={<AdminFaceLivenessReviewsPage />} />
                <Route path="/admin/face-liveness-reviews/:userId" element={<AdminFaceLivenessReviewDetailPage />} />
                <Route path="/admin/business-profiles" element={<AdminBusinessProfilesPage />} />
                <Route path="/admin/payout-reviews" element={<AdminPayoutReviewsPage />} />
                <Route path="/admin/support" element={<AdminSupportPage />} />
                <Route path="/admin/whatsapp-bookings" element={<AdminWhatsAppBookingsPage />} />
                <Route path="/admin/trips" element={<AdminTripsPage />} />
                <Route path="/admin/trips/:tripId" element={<AdminTripDetailPage />} />
                <Route path="/admin/driver-coverage" element={<AdminDriverCoveragePage />} />
                <Route path="/admin/driver-onboarding" element={<AdminDriverOnboardingAssistPage />} />
                <Route path="/admin/drivers" element={<AdminDriversPage />} />
                <Route path="/admin/drivers/:driverId" element={<AdminDriverReviewPage />} />
                <Route path="/admin/users" element={<AdminUsersPage />} />
            </Route>
            <Route element={<AdminRoute requireSuperAdmin deniedArea="finance" />}>
                <Route path="/admin/finance" element={<AdminFinancePage />} />
            </Route>
            <Route element={<AdminRoute requireSuperAdmin deniedArea="payment operations" />}>
                <Route path="/admin/payment-ops" element={<AdminPaymentOpsPage />} />
                <Route path="/admin/payment-ops/:publicId" element={<AdminPaymentIntentDetailPage />} />
            </Route>
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
};

export default AppRouter;
